import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Camera, Calendar, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { API } from "@/config/constants";

const ITEMS_PER_PAGE = 9;

const PhotoGalleryPage = () => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const response = await axios.get(`${API}/photo-gallery`);
        setPhotos(response.data);
      } catch (error) {
        toast.error("Failed to load photos");
      } finally {
        setLoading(false);
      }
    };
    fetchPhotos();
  }, []);

  return (
    <div className="max-w-7xl mx-auto page-content" data-testid="gallery-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white">Photo Gallery</h1>
          <p className="text-xs sm:text-base text-white/70 mt-1">All your nature photography in one place</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Camera size={18} weight="duotone" className="text-white" />
          <span className="text-white font-medium">{photos.length} photos</span>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <Skeleton key={i} className="aspect-square bg-[#EAE7DE] rounded-lg" />
          ))}
        </div>
      ) : photos.length === 0 ? (
        <div className="text-center py-16">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/d79f4e12-49d2-4273-980e-fb3c434a8417/images/f2b7626d87726d30e16e60289a6d933c9009f9780fa28acc64676ce5fd3fda67.png"
            alt="Empty gallery"
            className="w-48 h-48 mx-auto object-contain opacity-80 mb-4"
          />
          <p className="font-handwritten text-2xl text-[#7C8068] mb-4">No photos yet!</p>
          <p className="text-[#7C8068] mb-6">Start capturing your nature observations.</p>
          <Button
            onClick={() => navigate("/entry/new")}
            className="bg-[#C25A3C] hover:bg-[#A84B32] text-white"
          >
            <Camera size={18} className="mr-2" />
            Create Entry with Photo
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {photos.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map((photo, idx) => (
            <div
              key={`${photo.photo.id}-${idx}`}
              className="group relative aspect-square overflow-hidden rounded-lg border border-[#D2C2B3] cursor-pointer card-hover shadow-[0_6px_24px_rgba(50,50,50,0.5)]"
              onClick={() => setSelectedPhoto(photo)}
            >
              <img
                src={`${API}/photos/${photo.photo.storage_path}`}
                alt={photo.entry_title}
                className="w-full h-full object-cover bg-white transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-white font-medium text-sm truncate">{photo.entry_title}</p>
                  {photo.entry_location && (
                    <p className="text-white/70 text-xs flex items-center gap-1">
                      <MapPin size={10} />
                      {photo.entry_location}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
          </div>
          {photos.length > ITEMS_PER_PAGE && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo(0, 0); }}
                disabled={page === 1}
                className="bg-[#55624F] border-[#55624F] text-white hover:bg-[#424D3D] disabled:opacity-30"
                data-testid="photos-prev-btn"
              >
                <CaretLeft size={18} className="mr-1" />Previous
              </Button>
              <span className="text-white text-sm font-['Manrope']">
                {page} / {Math.ceil(photos.length / ITEMS_PER_PAGE)}
              </span>
              <Button
                variant="outline"
                onClick={() => { setPage(p => Math.min(Math.ceil(photos.length / ITEMS_PER_PAGE), p + 1)); window.scrollTo(0, 0); }}
                disabled={page >= Math.ceil(photos.length / ITEMS_PER_PAGE)}
                className="bg-[#55624F] border-[#55624F] text-white hover:bg-[#424D3D] disabled:opacity-30"
                data-testid="photos-next-btn"
              >
                Next<CaretRight size={18} className="ml-1" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Photo Lightbox */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="bg-white border-[#D2C2B3] max-w-4xl p-0 overflow-hidden">
          {selectedPhoto && (
            <div className="flex flex-col md:flex-row">
              <div className="flex-1 bg-black flex items-center justify-center max-h-[60vh] md:max-h-[80vh]">
                <img
                  src={`${API}/photos/${selectedPhoto.photo.storage_path}`}
                  alt={selectedPhoto.entry_title}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="w-full md:w-72 p-4 border-t md:border-t-0 md:border-l border-[#D2C2B3]">
                <h3 className="font-heading text-lg font-semibold text-[#2E332B] mb-2">
                  {selectedPhoto.entry_title}
                </h3>
                {selectedPhoto.entry_location && (
                  <p className="text-sm text-[#7C8068] flex items-center gap-1 mb-2">
                    <MapPin size={14} />
                    {selectedPhoto.entry_location}
                  </p>
                )}
                <p className="text-sm text-[#7C8068] flex items-center gap-1 mb-4">
                  <Calendar size={14} />
                  {new Date(selectedPhoto.entry_date).toLocaleDateString()}
                </p>
                <Button
                  onClick={() => {
                    setSelectedPhoto(null);
                    navigate(`/entry/${selectedPhoto.entry_id}`);
                  }}
                  className="w-full bg-[#55624F] hover:bg-[#424D3D] text-white"
                >
                  View Entry
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhotoGalleryPage;
